import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Plus, MapPin, Calendar, Users, Trash2, Copy, Phone, Mail, Crown, Search } from "lucide-react";
import { toast } from "sonner";

export default function PortalPage() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [prefs, setPrefs] = useState(null);
  const [recallQuery, setRecallQuery] = useState("");
  const [recallBusy, setRecallBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login?next=/portal");
      return;
    }
    api.get("/itineraries").then((r) => {
      setItems(r.data);
      setBusy(false);
    });
    if (user.membership_tier) {
      api.get("/me/preferences").then((r) => setPrefs(r.data)).catch(() => {});
    }
  }, [user, loading, navigate]);

  const remove = async (id) => {
    if (!confirm("Delete this itinerary?")) return;
    try {
      await api.delete(`/itineraries/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Removed.");
    } catch (e) {
      toast.error("Could not delete.");
    }
  };

  const recallTrip = async (e) => {
    e.preventDefault();
    const code = recallQuery.trim().toUpperCase();
    if (!code) return;
    setRecallBusy(true);
    try {
      const r = await api.get(`/itineraries/by-recall/${code}`);
      navigate(`/itinerary/${r.data.id}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "No trip found for that reference. Double-check the code or speak with our concierge.",
      );
    } finally {
      setRecallBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="portal-page">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="overline">Client portal</div>
          <h1 className="font-display text-5xl text-[var(--emerald)] mt-3">
            Welcome back, <em className="text-[var(--gold)]">{user.name}</em>.
          </h1>
          <p className="text-[var(--ink-2)] mt-3">
            Your saved journeys — past, present, and beautifully forthcoming.
          </p>
          {user.client_id && (
            <div
              className="mt-3 inline-flex items-center gap-2 px-3 py-1 border border-[var(--emerald)]/30 text-xs uppercase tracking-[0.2em] text-[var(--emerald)] mr-2"
              data-testid="portal-client-id"
              title="Your durable Janté Client ID — quote this when speaking with our concierge team."
            >
              Client ID · <span className="tabular-nums font-medium">{user.client_id}</span>
            </div>
          )}
          {user.membership_number && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 border border-[var(--gold)] text-xs uppercase tracking-[0.2em] text-[var(--emerald)] mr-2" data-testid="portal-member-number">
              <Crown size={12} strokeWidth={1.25} className="text-[var(--gold)]" />
              Client No. {user.membership_number}
            </div>
          )}
          {user.membership_tier && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 border border-[var(--gold)] text-xs uppercase tracking-[0.2em] text-[var(--emerald)]">
              <Crown size={12} strokeWidth={1.25} className="text-[var(--gold)]" />
              {user.membership_tier} member
            </div>
          )}
        </div>
        <button onClick={() => navigate("/chat")} className="btn-primary" data-testid="new-trip-btn">
          <Plus size={14} strokeWidth={1.5} /> New trip with Sasha
        </button>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button
          onClick={() => navigate("/groups/new")}
          className="text-xs uppercase tracking-[0.18em] text-[var(--brand-red)] hover:text-[var(--emerald)] inline-flex items-center gap-2 border border-[var(--brand-red)]/40 hover:border-[var(--emerald)] px-4 py-2.5"
          data-testid="portal-start-group-btn"
        >
          <Users size={14} strokeWidth={1.5} /> Start a group trip
        </button>
      </div>

      {/* Recall a trip by reference number — temporarily hidden per concierge
          ops decision; backend endpoints (/api/recall, /itineraries/by-recall)
          are still live so we can re-enable instantly when ready. */}
      {false && (
      <form
        onSubmit={recallTrip}
        className="lux-card mt-10 flex flex-wrap items-end gap-4"
        data-testid="recall-form"
      >
        <div className="flex-1 min-w-[260px]">
          <div className="overline mb-1">Recall a trip</div>
          <div className="relative">
            <Search
              size={14}
              strokeWidth={1.5}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]"
            />
            <input
              className="lux-input !pl-9 uppercase tracking-wide"
              placeholder="JT-XXXX-NN"
              value={recallQuery}
              onChange={(e) => setRecallQuery(e.target.value)}
              data-testid="recall-input"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={recallBusy || !recallQuery.trim()}
          className="btn-primary"
          data-testid="recall-submit"
        >
          {recallBusy ? "Searching…" : "Recall trip"}
        </button>
        <p className="text-[11px] text-[var(--muted)] basis-full">
          Enter the trip reference number Sasha gave you and we'll pull every detail.
        </p>
      </form>
      )}

      {/* Member-only quick info */}
      {user.membership_tier && (
        <div className="grid md:grid-cols-3 gap-6 mt-10" data-testid="member-quick-info">
          <div className="lux-card">
            <div className="overline">Total trips</div>
            <div className="font-display text-5xl text-[var(--emerald)] mt-2">{items.length}</div>
            <p className="text-sm text-[var(--ink-2)] mt-1">across your portfolio</p>
          </div>
          <div className="lux-card">
            <div className="overline">Travel style</div>
            <div className="font-display text-3xl text-[var(--emerald)] mt-2 capitalize">
              {prefs?.travel_style || "—"}
            </div>
            <p className="text-sm text-[var(--ink-2)] mt-1">
              {prefs?.pace ? `${prefs.pace} pace` : "Set your preferences"}
            </p>
          </div>
          <div className="lux-card">
            <div className="overline">Companions</div>
            <div className="font-display text-3xl text-[var(--emerald)] mt-2">
              {(prefs?.family_members || []).length}
            </div>
            <p className="text-sm text-[var(--ink-2)] mt-1">
              {(prefs?.family_members || []).slice(0, 3).map((f) => f.name).join(", ") || "Add family on the membership page"}
            </p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {busy && (
          <div className="text-[var(--muted)] text-sm">Loading…</div>
        )}
        {!busy && items.length === 0 && (
          <div className="lux-card md:col-span-2 lg:col-span-3 text-center" data-testid="portal-empty">
            <div className="overline">Your portal is empty</div>
            <h3 className="font-display text-3xl text-[var(--emerald)] mt-3">
              Begin your first journey with Sasha.
            </h3>
            <p className="text-[var(--ink-2)] mt-3">She's already waiting.</p>
            <button onClick={() => navigate("/chat")} className="btn-primary mt-6">
              Talk to Sasha
            </button>
          </div>
        )}
        {items.map((it, i) => (
          <article key={it.id} className="lux-card flex flex-col" data-testid={`itinerary-card-${i}`}>
            <div className="aspect-[16/10] overflow-hidden -m-8 mb-4 bg-[var(--ivory-2)]">
              <img
                src={
                  it.cover_image ||
                  "https://images.unsplash.com/photo-1583844056361-4418a8f2a985?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxhbWFsZmklMjBjb2FzdCUyMGl0YWx5fGVufDB8fHx8MTc3NzE2Njk3OXww&ixlib=rb-4.1.0&q=85"
                }
                alt={it.destination}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="overline mt-2">{it.destination}</div>
            <h3 className="font-display text-2xl text-[var(--emerald)] mt-1">{it.title}</h3>
            {/* Recall chip hidden — see /portal recall form for context */}
            {false && it.recall_code && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs uppercase tracking-[0.18em] text-[var(--brand-red)]">
                  Recall · {it.recall_code}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(it.recall_code);
                    toast.success("Recall code copied.");
                  }}
                  className="text-[var(--gold)] hover:text-[var(--emerald)]"
                  aria-label="Copy recall code"
                  data-testid={`copy-recall-${i}`}
                >
                  <Copy size={11} strokeWidth={1.5} />
                </button>
              </div>
            )}
            <div className="text-sm text-[var(--ink-2)] mt-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <Calendar size={14} strokeWidth={1.25} className="text-[var(--gold)]" />
                {it.start_date} → {it.end_date}
              </div>
              <div className="flex items-center gap-2">
                <Users size={14} strokeWidth={1.25} className="text-[var(--gold)]" />
                {it.travelers} traveler{it.travelers > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={14} strokeWidth={1.25} className="text-[var(--gold)]" />
                {it.items?.length || 0} curated items
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <button
                onClick={() => navigate(`/itinerary/${it.id}`)}
                className="btn-primary flex-1"
                data-testid={`view-itinerary-${i}`}
              >
                View
              </button>
              <button
                onClick={() => remove(it.id)}
                className="btn-ghost"
                aria-label="Delete itinerary"
                data-testid={`delete-itinerary-${i}`}
              >
                <Trash2 size={14} strokeWidth={1.25} />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
